import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '@material-ui/core/styles';
import {
  makeStyles,
  withStyles,
  Dialog,
  DialogTitle,
  useMediaQuery,
} from '@material-ui/core';

import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
} from '@redshelf/redshelf-ui'; // Dialog from redshelf-ui transforms aria-labelledby into aria-label, which is incorrect. DialogTitle from redshelf-ui does not seem to support disableTypography prop
import { Typography } from '@redshelf/redshelf-ui';
import { UserContentItemModifiersContext } from 'contexts';
import { IconButton } from 'components';
import { CloseIcon } from 'icons';

const useStyles = makeStyles((theme) => ({
  firstChild: {
    paddingTop: `${theme.spacing(3)}px !important`,
  },
  closeButton: {
    color: theme.palette.grey[500],
  },
  title: {
    ...theme.typography.h6,
    padding: theme.spacing(3, 3, 1),
    color: theme.palette.greyBlue[700],
  },
  titleWithClose: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',

    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: 0,
  },
}));

const DialogContentMUI = withStyles((theme) => ({
  root: {
    padding: theme.spacing(1, 3, 2),
  },
}))(DialogContent);

const DialogContentTextMUI = withStyles((theme) => ({
  root: {
    ...theme.typography.body1,
    margin: '0',
  },
}))(DialogContentText);

const DialogActionsMUI = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2, 3, 3),
  },
}))(DialogActions);

const StyledButton = withStyles((theme) => ({
  root: {
    height: '100%',
  },
}))(Button);

const ConfirmationDialog = ({
  cancel,
  children,
  className,
  confirm,
  dialogActionsProps,
  dialogContentProps,
  fullScreen,
  id,
  isOpen,
  labelledById,
  message,
  onClose,
  shouldShowX = false,
  showTitle = true,
  size,
  swapButtons,
  title,
  titleComponent = 'h6',
  ...props
}) => {
  const { onDialogClose, onDialogOpen } = useContext(
    UserContentItemModifiersContext,
  );

  const classes = useStyles();
  const theme = useTheme();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.down('xs'));
  const fullScreenProp =
    typeof fullScreen === 'undefined' ? xsBreakpoint : fullScreen;
  const sizeProp = size ?? theme.breakpoints.keys[0];

  return (
    <Dialog
      className={clsx(classes.root, className)}
      aria-labelledby={labelledById}
      open={isOpen}
      onClose={(_, reason) => {
        // default MUI behaviour is to close on Escape key Down, but we want Dialog to close on keyUp not keyDown
        // so prevent close on KeyDown
        if (reason !== 'escapeKeyDown') {
          onClose ? onClose() : cancel.action();
        }
        onDialogClose(id);
      }}
      // add close on Escape Key Up
      onKeyUp={(e) => {
        e.stopPropagation();
        if (e.key === 'Escape') {
          onClose ? onClose() : cancel.action();
        }
        onDialogClose(id);
      }}
      maxWidth={sizeProp}
      fullWidth
      fullScreen={fullScreenProp}
      TransitionProps={{ onEntered: () => onDialogOpen(id) }}
      {...props}
    >
      <DialogTitle
        disableTypography
        hidden={!showTitle}
        classes={{ root: classes.title }}
        {...(shouldShowX ? { className: classes.titleWithClose } : {})}
      >
        <Typography id={labelledById} component={titleComponent} variant="h6">
          {title}
        </Typography>
        {shouldShowX ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              onClose();
              onDialogClose(id);
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      {children ? (
        children
      ) : (
        <DialogContentMUI
          className={showTitle ? '' : classes.firstChild}
          {...dialogContentProps}
        >
          <DialogContentTextMUI>{message}</DialogContentTextMUI>
        </DialogContentMUI>
      )}

      <DialogActionsMUI {...dialogActionsProps}>
        {cancel && !swapButtons && (
          <StyledButton
            onClick={() => {
              cancel.action();
              onDialogClose(id);
            }}
            variant="secondary"
          >
            {cancel.label}
          </StyledButton>
        )}

        {confirm && (
          <StyledButton
            onClick={() => {
              confirm.action();
              onDialogClose(id);
            }}
          >
            {confirm.label}
          </StyledButton>
        )}

        {cancel && swapButtons && (
          <StyledButton
            onClick={() => {
              cancel.action();
              onDialogClose(id);
            }}
            variant="secondary"
          >
            {cancel.label}
          </StyledButton>
        )}
      </DialogActionsMUI>
    </Dialog>
  );
};

ConfirmationDialog.propTypes = {
  cancel: PropTypes.shape({
    label: PropTypes.string,
    action: PropTypes.func,
  }),
  children: PropTypes.node,
  className: PropTypes.string,
  confirm: PropTypes.shape({
    label: PropTypes.string,
    action: PropTypes.func,
  }),
  dialogActionsProps: PropTypes.shape({}),
  dialogContentProps: PropTypes.shape({}),
  fullScreen: PropTypes.bool,
  isOpen: PropTypes.bool,
  labelledById: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // optional in case we want different behavior for cancel and dismiss
  onClose: PropTypes.func,
  shouldShowX: PropTypes.bool,
  showTitle: PropTypes.bool,
  size: PropTypes.string,
  swapButtons: PropTypes.bool,
  title: PropTypes.string.isRequired,
  titleComponent: PropTypes.string,
};

export default ConfirmationDialog;
